import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { DataService } from '../../services/data.service';

/**
 * NHS Agreement Guard — protects NHS users who haven't accepted terms.
 *
 * If user.login_service === 'nhs' and user.nhs_agreement !== 1, the user
 * has not completed the NHS agreement step (either /nhs/account/agreement
 * for new accounts or /nhs/match/success for matched accounts). Redirect
 * them to the appropriate agreement page based on where they came from.
 *
 * Origin is stored in localStorage by NhsCallbackPage as `nhs_origin`
 * (values: 'match' | 'new-account') immediately after callback redirect.
 * We also check `nhs_match_status` for backwards compat and the query
 * param that the backend now includes.
 *
 * Non-NHS users (login_service !== 'nhs') always pass.
 * Returns true if allowed, UrlTree if redirect needed.
 */
export const nhsAgreementGuard: CanActivateFn = (route, state) => {
  const data = inject(DataService);
  const router = inject(Router);

  const user: any = (data as any).user ?? null;

  // Not logged in yet (e.g. direct /accesscodes hit before fakesplash pulse) — let AuthGuard handle it.
  // We only block NHS users who are logged in but haven't agreed.
  if (!user) {
    // Try to read from storage fallback (DataService hydrates async, guard may run early)
    try {
      const raw = localStorage.getItem('user') || localStorage.getItem('userData') || sessionStorage.getItem('user');
      if (raw) {
        const parsed = JSON.parse(raw);
        const loginService = parsed?.login_service;
        const agreement = parsed?.nhs_agreement;
        if (loginService === 'nhs' && agreement != 1 && agreement !== '1' && agreement !== true) {
          return redirectForNhs(router);
        }
      }
    } catch {}
    return true;
  }

  const loginService = user.login_service ?? user.loginService;
  if (loginService !== 'nhs') {
    return true;
  }

  const agreement = user.nhs_agreement ?? user.nhsAgreement ?? user.nhs_agree;
  const isAgreed = agreement === 1 || agreement === '1' || agreement === true || agreement === 'true';

  if (isAgreed) {
    return true;
  }

  return redirectForNhs(router);
};

function redirectForNhs(router: Router) {
  // Where did this NHS login originate? Stored by NhsCallbackPage.
  let origin: string | null = null;
  try {
    origin = localStorage.getItem('nhs_origin') || localStorage.getItem('nhs_match_status');
  } catch {}

  // Read from current URL query as fallback (callback adds ?origin or match_status)
  if (!origin) {
    try {
      const params = new URLSearchParams(window.location.search);
      origin = params.get('nhs_origin') || params.get('match_status') || params.get('origin');
    } catch {}
  }

  if (origin === 'match') {
    // Matched account with pending agreement — the success screen is the
    // agreement gate for matches (it POSTs accept-agreement)
    return router.createUrlTree(['/nhs/match/success']);
  }

  // Default and 'new-account' both use the new-account agreement page
  return router.createUrlTree(['/nhs/account/agreement']);
}
